import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { Subscription, finalize } from 'rxjs';
import { NotificationRoute } from '@core/services/api/routes/tenancy/notification.route';
import { NotificationAlert, NotificationConfig, NotificationTab } from '@model/notification-center.model';
import { TenantPerm } from '@enum/tenant/permission/tenant-perm.enum';
import { PermissionService } from '@shared/services/permission.service';
import { Luxon } from 'src/app/helpers/luxon';

@Component({
  selector: 'mg-notification-center',
  imports: [FormsModule, MatButton, MatIconButton, MatIcon, MatProgressSpinner, MatSlideToggle],
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  private readonly api = inject(NotificationRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialogRef<NotificationCenterComponent>);
  private readonly permissions = inject(PermissionService);
  private readonly subscriptions = new Subscription();
  private listing?: Subscription;

  protected readonly canConfigure = this.permissions.checkPermissions([TenantPerm.Accounts_View])
    && this.permissions.checkPermissions([TenantPerm.Accounts_Update]);
  protected readonly tabs: { value: NotificationTab; label: string }[] = [
    { value: 'pending', label: 'Pendentes' }, { value: 'unread', label: 'Não lidas' }, { value: 'history', label: 'Histórico' },
  ];
  protected tab: NotificationTab = 'pending';
  protected alerts: NotificationAlert[] = [];
  protected loading = true;
  protected error = '';
  protected actionError = '';
  protected syncedAt: string | null = null;
  protected stale = false;
  protected page = 1;
  protected lastPage = 1;
  protected total = 0;
  protected unread = 0;
  protected markingAll = false;
  protected readonly marking = new Set<string>();
  protected configuring = false;
  protected configs: NotificationConfig[] = [];
  protected configLoading = false;
  protected configError = '';
  protected readonly savingConfig = new Set<string>();
  protected readonly configLabels: Record<string, string> = {
    account_payable_notify_upcoming_due_date: 'Pagamentos próximos',
    account_payable_notify_overdue: 'Pagamentos em atraso',
    account_payable_notify_upcoming_due_date_days: 'Antecedência de pagamentos (dias)',
    account_receivable_notify_upcoming_due_date: 'Recebimentos previstos',
    account_receivable_notify_overdue: 'Recebimentos em atraso',
    account_receivable_notify_upcoming_due_date_days: 'Antecedência de recebimentos (dias)',
  };

  ngOnInit(): void { this.load(); }

  ngOnDestroy(): void {
    this.listing?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  protected load(page = this.page): void {
    this.listing?.unsubscribe();
    this.page = page;
    this.loading = true;
    this.error = '';
    this.listing = this.api.center(this.tab, page).pipe(finalize(() => {
      this.loading = false;
      this.cdr.markForCheck();
    })).subscribe({
      next: response => {
        if (response.sync_status === 'stale' && !response.synced_at) {
          this.error = 'Não foi possível carregar as notificações.';
          return;
        }
        this.alerts = response.data;
        this.page = response.meta.current_page;
        this.lastPage = response.meta.last_page;
        this.total = response.meta.total;
        this.unread = response.meta.unread_count;
        this.syncedAt = response.synced_at;
        this.stale = response.sync_status === 'stale';
        if (this.alerts.length === 0 && this.page > 1 && this.lastPage < this.page) this.load(this.lastPage);
      },
      error: () => { this.error = 'Não foi possível carregar as notificações.'; },
    });
  }

  protected selectTab(tab: NotificationTab): void {
    this.tab = tab;
    this.actionError = '';
    this.load(1);
  }

  protected markRead(alert: NotificationAlert): void {
    if (alert.read || this.marking.has(alert.id) || this.markingAll) return;
    this.marking.add(alert.id);
    this.actionError = '';
    this.subscriptions.add(this.api.readCenter(alert.id).pipe(finalize(() => {
      this.marking.delete(alert.id);
      this.cdr.markForCheck();
    })).subscribe({
      // O usuário pode ter trocado de aba durante o POST. Reconsultar evita
      // decrementar duas vezes a contagem recebida por um GET mais recente.
      next: () => this.load(),
      error: () => { this.actionError = 'Não foi possível marcar como lida. Tente novamente.'; },
    }));
  }

  protected markAll(): void {
    if (this.markingAll || this.marking.size || !this.unread) return;
    this.markingAll = true;
    this.actionError = '';
    this.subscriptions.add(this.api.readAllCenter().pipe(finalize(() => {
      this.markingAll = false;
      this.cdr.markForCheck();
    })).subscribe({
      next: () => this.load(),
      error: () => { this.actionError = 'Não foi possível marcar todas como lidas. Tente novamente.'; },
    }));
  }

  protected open(alert: NotificationAlert): void {
    // A API só entrega rotas do produto. Não navegar para URL externa mesmo com payload inválido.
    if (!alert.action_url.startsWith('/painel/')) return;
    if (this.router.url === alert.action_url) { this.dialog.close(); return; }
    void this.router.navigateByUrl(alert.action_url).then(navigated => {
      if (navigated) this.dialog.close();
    }).catch(() => {
      this.actionError = 'Não foi possível abrir esta área. Tente novamente.';
      this.cdr.markForCheck();
    });
  }

  protected close(): void { this.dialog.close(); }

  protected date(value: string): string { return Luxon.from(value).toFormat('dd/MM/yyyy'); }
  protected time(value: string): string { return Luxon.from(value).toFormat('dd/MM HH:mm'); }

  protected money(value: string): string {
    // Formata a string decimal sem perder centavos ao converter montantes grandes para Number.
    const match = /^(-?)(\d+)\.(\d{2})$/.exec(value);
    if (!match) return 'Valor indisponível';
    return `${match[1]}R$ ${match[2].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${match[3]}`;
  }

  protected quantity(alert: NotificationAlert): string {
    const noun = alert.kind === 'governance' ? 'ocorrência'
      : alert.kind === 'invoice_failed' ? 'nota' : alert.kind === 'charge_failed' ? 'cobrança' : 'conta';
    return `${alert.count} ${noun}${alert.count === 1 ? '' : 's'}`;
  }

  protected severity(alert: NotificationAlert): string {
    return alert.resolved_at ? 'Resolvida' : ({ critical: 'Urgente', warning: 'Atenção', info: 'Previsto' }[alert.severity]);
  }

  protected icon(alert: NotificationAlert): string {
    if (alert.resolved_at) return 'check_circle';
    if (alert.kind.startsWith('funding_')) return 'payments';
    return { critical: 'error', warning: 'warning', info: 'schedule' }[alert.severity];
  }

  protected configure(): void {
    if (!this.canConfigure) return;
    this.configuring = !this.configuring;
    if (!this.configuring) { this.load(1); return; }
    this.loadConfig();
  }

  protected loadConfig(): void {
    this.configLoading = true;
    this.configError = '';
    this.subscriptions.add(this.api.notificationConfig().pipe(finalize(() => {
      this.configLoading = false;
      this.cdr.markForCheck();
    })).subscribe({
      next: response => { this.configs = response; },
      error: () => { this.configError = 'Não foi possível carregar as configurações.'; },
    }));
  }

  protected isDays(config: NotificationConfig): boolean { return config.action.endsWith('_days'); }

  protected toggleConfig(config: NotificationConfig, event: MatSlideToggleChange): void {
    const requested = event.checked;
    event.source.checked = config.value === true;
    this.saveConfig(config, requested);
  }

  protected saveConfig(config: NotificationConfig, value: boolean | number): void {
    if (this.savingConfig.has(config.action)) return;
    if (this.isDays(config) && (!Number.isInteger(value) || Number(value) < 0 || Number(value) > 365)) {
      this.configError = 'Informe uma antecedência entre 0 e 365 dias.';
      return;
    }
    this.savingConfig.add(config.action);
    this.configError = '';
    this.subscriptions.add(this.api.updateNotificationConfig(config.action, value).pipe(finalize(() => {
      this.savingConfig.delete(config.action);
      this.cdr.markForCheck();
    })).subscribe({
      next: () => { config.value = value; },
      error: () => { this.configError = 'Não foi possível salvar. A configuração anterior foi mantida.'; },
    }));
  }
}
